import cn from 'classnames';
import { createContext, useContext, useRef, useMemo } from 'react';
import { throttle, debounce } from 'throttle-debounce';

const createBemBlockBuilder = (blocks)=>{
    const cleanedBlocks = blocks.reduce((reviewedBlocks, block)=>block ? reviewedBlocks.concat(block.trim().split(" ")) : reviewedBlocks, []);
    return (elementOrModifier = "")=>cleanedBlocks.reduce((classList, block)=>cn(classList, `${block}${elementOrModifier}`), "");
};

function createGenericContext({ initialContext, errorMessage }) {
    const GenericContext = /*#__PURE__*/ createContext(initialContext);
    const useGenericContext = ()=>{
        const expectedContext = useContext(GenericContext);
        if (!expectedContext) {
            const message = errorMessage ? errorMessage : "useGenericContext - Can not provide a context to use. Please make sure you have defined a context to be consumed.";
            throw new Error(message);
        }
        return expectedContext;
    };
    const GenericContextConsumer = ({ children })=>{
        const expectedContext = useGenericContext();
        return children(expectedContext);
    };
    return {
        useGenericContext,
        GenericContextProvider: GenericContext.Provider,
        GenericContextConsumer
    };
}

const DEFAULT_DELAY = 300;
const DEFAULT_INPUT_MIN_LENGTH = 7;
const DEFAULT_SHOULD_THROTTLE = (inputValue)=>inputValue.length < DEFAULT_INPUT_MIN_LENGTH || inputValue.endsWith(" ");
const throttleDebounceInput = (ref)=>{
    const { delay = DEFAULT_DELAY, shouldThrottle = DEFAULT_SHOULD_THROTTLE } = ref.current[1];
    const throttledCallback = throttle(delay, (inputValue)=>ref.current[0](inputValue));
    const debouncedCallback = debounce(delay, (inputValue)=>ref.current[0](inputValue));
    return (inputValue)=>{
        const condition = typeof shouldThrottle === "function" ? shouldThrottle(inputValue) : shouldThrottle;
        if (condition) {
            throttledCallback(inputValue);
        } else {
            debouncedCallback(inputValue);
        }
    };
};
/**
 * @param func Callback function to be debounced or throttled
 * @param options options.delay: amount of time to delay the throttle or debounce,
 *  options.shouldThrottle: boolean deciding to use throttle or debouncing.
 *  _Note: This cannot currently be changed after initial render due to memoization._
 * @returns Memoized version of the func callback wrapped in debounce/throttle methods
 * @description Within this function a ref is utilized to ensure that every call to useThrottledDebouncedInput does not re-memoize.
 *  We memoize a reference to the func and options properties being passed as parameters.
 *  Therefore, we do not want to re-memoize if the func changes, but the reference will always point
 *  to the current func value.
 */ const useThrottledDebouncedInput = (func, options = {})=>{
    const paramsRef = useRef([
        func,
        options
    ]);
    paramsRef.current = [
        func,
        options
    ];
    return useMemo(()=>throttleDebounceInput(paramsRef), []);
};

export { createBemBlockBuilder, createGenericContext, useThrottledDebouncedInput };
//# sourceMappingURL=utils-esm.js.map
